/**
 * The medicines the patient will take after this visit. Starts as what they take now; the
 * provider changes doses (or applies a suggestion) and sees each change marked before saving.
 */
import {
  type DrugDef,
  type Regimen,
  dailyOf,
  defaultTimes,
  regimenFor,
  sameRegimenItem,
} from '@assist/core';
import { useState } from 'preact/hooks';
import { t } from '../i18n.js';

const num = (n: number) => String(Math.round(n * 100) / 100);

export function RegimenEditor(props: {
  drugs: DrugDef[];
  before: Regimen;
  regimen: Regimen;
  onChange: (r: Regimen) => void;
}) {
  const [extra, setExtra] = useState<string[]>([]);
  const codes = props.drugs
    .map((d) => d.code)
    .filter((c) => c in props.before || c in props.regimen || extra.includes(c));
  const rows = props.drugs.filter((d) => codes.includes(d.code));
  const addable = props.drugs.filter((d) => !codes.includes(d.code));

  const setDaily = (d: DrugDef, daily: number) => {
    const next = { ...props.regimen };
    const item = regimenFor(d, daily);
    const keepTimes = props.regimen[d.code]?.timesPerDay;
    if (item && keepTimes && daily > 0)
      next[d.code] = { dose: daily / keepTimes, timesPerDay: keepTimes };
    else if (item) next[d.code] = item;
    else delete next[d.code];
    props.onChange(next);
  };
  const setTimes = (d: DrugDef, times: number) => {
    const daily = dailyOf(props.regimen[d.code]);
    if (!daily) return;
    props.onChange({ ...props.regimen, [d.code]: { dose: daily / times, timesPerDay: times } });
  };

  return (
    <div class="regimen">
      {rows.length === 0 ? <p class="muted">{t.noMedicines}</p> : null}
      {rows.map((d) => {
        const now = props.regimen[d.code];
        const was = props.before[d.code];
        const daily = dailyOf(now);
        const options = [...new Set([...d.doses, daily, dailyOf(was)])].sort((a, b) => a - b);
        const changed = !sameRegimenItem(was, now);
        return (
          <div key={d.code} class={changed ? 'med changed' : 'med'} data-drug={d.code}>
            <div class="med-name">
              <strong>{d.display}</strong>
              <span class="muted">
                {' '}
                {t.current}: {was ? `${num(dailyOf(was))} ${d.unit}/${t.perDay}` : '—'}
              </span>
              {changed ? <span class="tag">{t.changed}</span> : null}
            </div>
            <div class="med-controls">
              <select
                aria-label={`${d.display} ${t.perDay}`}
                value={String(daily)}
                onChange={(e) => setDaily(d, Number((e.target as HTMLSelectElement).value))}
              >
                {options.map((n) => (
                  <option key={n} value={String(n)}>
                    {n === 0 ? '—' : `${num(n)} ${d.unit} ${t.perDay}`}
                  </option>
                ))}
              </select>
              {daily > 0 ? (
                <select
                  aria-label={`${d.display} ${t.timesPerDay}`}
                  value={String(now?.timesPerDay ?? defaultTimes(d, daily))}
                  onChange={(e) => setTimes(d, Number((e.target as HTMLSelectElement).value))}
                >
                  {[1, 2, 3].map((n) => (
                    <option key={n} value={String(n)}>
                      {num(daily / n)} {d.unit} {t.dosing(n)}
                    </option>
                  ))}
                </select>
              ) : null}
              {daily > 0 ? (
                <button type="button" class="ghost small" onClick={() => setDaily(d, 0)}>
                  {t.stopMedicine}
                </button>
              ) : null}
            </div>
            {d.note && changed ? <div class="help">{d.note}</div> : null}
          </div>
        );
      })}
      {addable.length ? (
        <select
          aria-label={t.addMedicine}
          value=""
          onChange={(e) => {
            const code = (e.target as HTMLSelectElement).value;
            if (code) setExtra([...extra, code]);
          }}
        >
          <option value="">{t.addMedicine}</option>
          {addable.map((d) => (
            <option key={d.code} value={d.code}>
              {d.display}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}
